import { LitElement, html } from "lit";
import { renderQuestion } from "./quiz/quiz-question";
import { isMp4Media, resolvedMediaUrl } from "./media-utils";
import {
  attemptHeaders,
  loadAttemptReference,
  removeAttemptToken,
  removeAttemptReference,
  storeAttemptReference,
  storeAttemptToken,
} from "./attempt-token-store";
import { quizCreateError } from "./quiz-create-error";

// CSRF helpers for same-origin requests
const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "";
const jsonHeaders = () => ({ "Content-Type": "application/json", "X-CSRF-Token": csrfToken() });

class QuizApp extends LitElement {
  static properties = {
    state: { type: String },
    quiz: { type: Object },
    loading: { type: Boolean },
    error: { type: String },
    currentAnswer: { type: String },
    timerTick: { type: Number },
    showDetailIndex: { type: Number },
    finishConfirmationOpen: { type: Boolean },
  };

  constructor() {
    super();
    this.state = "intro"; // intro | running | summary
    this.quiz = null;
    this.loading = false;
    this.error = null;
    this.currentAnswer = null; // selected option for current question
    this.timerTick = 0; // increments each second for re-render
    this._timerId = null;
    this.showDetailIndex = null;
    this.finishConfirmationOpen = false;
    this._finishTrigger = null;
    // Simple action mutex to prevent races (e.g., timeout vs user click)
    this._actionLock = false;
    // When timeout fires during an ongoing action, queue it to run right after
    this._queuedTimeoutSeconds = 0;

    // Handler klawiatury
    this._onKeydown = this._onKeydown.bind(this);
  }

  createRenderRoot() {
    // Render in light DOM so Tailwind styles apply
    return this;
  }

  get userId() {
    return this.dataset.userId || null;
  }

  get lastQuizId() {
    return this.dataset.lastQuizId || null;
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener("keydown", this._onKeydown);

    // Try to continue last quiz from localStorage or dataset
    const localLast = loadAttemptReference("last_quiz_id");
    const lastId = this.lastQuizId || localLast;
    if (lastId) {
      this.continueQuiz(lastId);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener("keydown", this._onKeydown);
    this._clearTimer();
  }

  render() {
    return html`
      <div class="max-w-4xl mx-auto space-y-4">
        ${this.error ? this._renderError() : null}

        <div class="bg-white border rounded-xl shadow-sm overflow-hidden">
          ${this._renderHeader()}

          <div class="p-5">
            ${this.state === "intro" ? this._renderIntro() : null}
            ${this.state === "running" ? this._renderRunning() : null}
            ${this.state === "summary" ? this._renderSummary() : null}
          </div>
        </div>
      </div>
      ${this._renderFinishDialog()}
    `;
  }

  updated(changedProperties) {
    if (changedProperties.has("finishConfirmationOpen") && this.finishConfirmationOpen) {
      this.updateComplete.then(() => this.querySelector("[data-finish-cancel]")?.focus());
    }
  }

  _renderHeader() {
    const total = this.quiz?.total_questions || 0;
    const idx = this.quiz?.current_question_index || 0;
    const hasTimeLimit = Number(this.quiz?.duration_seconds || 0) > 0;
    const inProgress = this.state === "running" && total > 0;

    return html`
      <div class="px-5 pt-4 pb-4 border-b border-gray-100 bg-gray-50/60">
        <div class="flex items-start justify-between gap-3">
          <div>
            <h1 class="text-2xl font-semibold tracking-tight text-gray-900">
              Test
            </h1>
            <p class="mt-1 text-sm text-gray-500">
              Odpowiadaj na pytania po kolei. Możesz przerwać i wrócić do quizu w
              dowolnym momencie.
            </p>
          </div>

          ${inProgress
              ? html`
                <div class="flex flex-col items-end gap-2">
                  <div
                      class="inline-flex items-center gap-2 rounded-full bg-primary-50 px-3 py-1 text-xs font-medium text-primary-700 whitespace-nowrap"
                  >
                    <span
                        class="h-2 w-2 rounded-full bg-primary-500 animate-pulse"
                    ></span>
                    W trakcie
                  </div>
                  <div class="text-xs text-gray-600">
                    Pytanie
                    <span class="font-semibold"
                    >${Math.min(idx + 1, total)}</span
                    >
                    <span class="text-gray-400">/ ${total}</span>
                  </div>
                </div>
              `
              : html`
                <div
                    class="hidden sm:flex flex-col items-end text-right text-xs text-gray-500"
                >
                  <span>Gotowy, aby zacząć?</span>
                </div>
              `}
        </div>

        ${this.state === "running" && hasTimeLimit
            ? html`<div class="mt-3">${this._renderTimerBar()}</div>`
            : null}
      </div>
    `;
  }

  _renderTimerBar() {
    const total = Number(this.quiz?.duration_seconds || 0);
    const remaining = this._currentRemainingSeconds();
    const progress =
        total > 0
            ? Math.max(0, Math.min(100, Math.round(((total - remaining) / total) * 100)))
            : 0;

    return html`
      <div class="flex items-center gap-3">
        <progress class="attempt-progress h-2 flex-1" max="100" .value=${progress}>${progress}%</progress>
        <div
            class="inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700"
        >
          <span
              class="inline-block h-2 w-2 rounded-full bg-primary animate-pulse"
          ></span>
          <span>Pozostało ${this._formatDuration(remaining)}</span>
        </div>
      </div>
    `;
  }

  _renderError() {
    return html`
      <div
          role="status"
          aria-live="polite"
          class="mt-2 flex items-center gap-2 rounded-md bg-red-50 text-red-800 border border-red-200 px-4 py-3 text-sm"
      >
        <span>${this.error}</span>
        <button
            class="ml-auto text-red-700 underline"
            @click=${() => (this.error = null)}
        >
          Ukryj
        </button>
      </div>
    `;
  }

  _renderIntro() {
    return html`
      <div class="space-y-4">
        <p class="text-gray-700 leading-relaxed">
          Witaj w teście. Składa się on z wybranych losowo pytań z różnych
          sekcji. Możesz przerwać w dowolnym momencie i wrócić do testu
          później.
        </p>
        <div class="flex flex-wrap gap-3">
          <button
              class="px-4 py-2 rounded-md bg-primary text-white hover:bg-primary-600"
              ?disabled=${this.loading}
              @click=${() => this.startQuiz()}
          >
            Rozpocznij test
          </button>
          <button
              class="px-4 py-2 rounded-md bg-gray-100 hover:bg-gray-200"
              @click=${() => this.continueLast()}
          >
            Kontynuuj ostatni
          </button>
        </div>
      </div>
    `;
  }

  _renderRunning() {
    if (!this.quiz || this.quiz.status !== "in_progress") return null;

    const q = this.quiz?.current_question;
    const idx = this.quiz?.current_question_index || 0;
    const answers = this.quiz?.answers || {};
    const total = this.quiz?.total_questions || 0;
    const selected = this.currentAnswer ?? answers[String(idx)] ?? null;
    const canGoBack = idx > 0;
    const isLastQuestion = idx + 1 >= total;

    const busy = this.loading || this._actionLock;

    return html`
      <div class="space-y-6">
        ${renderQuestion({
          q,
          selected,
          readOnly: busy,
          onSelect: (opt) => {
            this.currentAnswer = opt;
            this.requestUpdate();
          },
        })}

        <div
            class="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-between gap-3 pt-4 border-t border-gray-100"
        >
          <div class="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <button
                type="button"
                class="btn btn-muted w-full sm:w-auto"
                ?disabled=${busy}
                @click=${() => this.interrupt()}
            >
              Przerwij test
            </button>
            ${!this.loading
                ? html`
                  <button
                      type="button"
                      class="btn ${isLastQuestion ? "btn-primary" : "btn-muted"} w-full sm:w-auto"
                      ?disabled=${busy}
                      @click=${(event) => this.openFinishConfirmation(event)}
                  >
                    Zakończ test
                  </button>
                `
                : null}
          </div>

          <div class="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <button
                type="button"
                class="btn w-full sm:w-auto"
                ?disabled=${busy || !canGoBack}
                @click=${() => this.prev()}
            >
              Poprzednie pytanie
            </button>

            ${!isLastQuestion
                ? html`
                  <button
                      type="button"
                      class="btn btn-primary w-full sm:w-auto"
                      ?disabled=${busy}
                      @click=${() => this.next()}
                  >
                    Następne pytanie
                  </button>
                `
                : null}
          </div>
        </div>
      </div>
    `;
  }

  _renderFinishDialog() {
    if (!this.finishConfirmationOpen || this.state !== "running" || !this.quiz) return null;
    const busy = this.loading || this._actionLock;

    return html`
      <div class="fixed inset-0 z-50 flex items-center justify-center bg-gray-950/50 p-4">
        <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="quiz-finish-title"
            aria-describedby="quiz-finish-description"
            aria-busy=${busy ? "true" : "false"}
            data-finish-dialog
            class="w-full max-w-lg max-h-[calc(100vh-2rem)] overflow-y-auto rounded-xl bg-white p-5 shadow-xl"
        >
          <h2 id="quiz-finish-title" class="text-lg font-semibold text-gray-900">
            Czy na pewno chcesz zakończyć test?
          </h2>
          <p id="quiz-finish-description" class="mt-3 text-sm leading-6 text-gray-700">
            Po zakończeniu nie będzie można wrócić do pytań. Nieudzielone odpowiedzi pozostaną bez odpowiedzi.
            Po zapisaniu testu wyświetlimy wynik.
          </p>
          ${this.error
              ? html`<p class="mt-3 text-sm text-red-700" role="status" aria-live="polite">${this.error}</p>`
              : null}
          <div class="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button
                type="button"
                class="btn btn-muted w-full sm:w-auto"
                data-finish-cancel
                ?disabled=${busy}
                @click=${() => this.closeFinishConfirmation()}
            >
              Wróć do testu
            </button>
            <button
                type="button"
                class="btn btn-primary w-full sm:w-auto"
                ?disabled=${busy}
                @click=${() => this.confirmFinish()}
            >
              ${busy ? "Kończenie testu…" : "Zakończ i pokaż wynik"}
            </button>
          </div>
        </div>
      </div>
    `;
  }

  _renderSummary() {
    const answers = this.quiz?.answers || {};
    const questions = this.quiz?.questions || [];
    const points = this.quiz?.points ?? 0;
    const max = this.quiz?.max_points ?? this.quiz?.total_questions ?? questions.length;
    const passed = this.quiz?.passed;
    const percent = max > 0 ? Math.round((points / max) * 100) : 0;

    return html`
      <div class="space-y-6">
        <!-- Główna karta wyniku -->
        <div
            class="rounded-2xl bg-gradient-to-r from-primary-500 to-primary-400 text-white px-5 py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
        >
          <div>
            <div class="text-sm uppercase tracking-wide text-white/80">
              Twój wynik
            </div>
            <div class="mt-1 flex items-baseline gap-2">
              <span class="text-3xl font-semibold leading-none">
                ${points} / ${max}
              </span>
              <span class="text-sm text-white/80">
                (${percent}%)
              </span>
            </div>
          </div>

          <div class="flex flex-col items-start sm:items-end gap-2">
            <span
                class="
                inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium
                ${passed
                    ? "bg-white/10 text-emerald-50 ring-1 ring-emerald-300/50"
                    : "bg-white/10 text-red-50 ring-1 ring-red-300/50"}
              "
            >
              <span
                  class="h-2 w-2 rounded-full ${passed
                      ? "bg-emerald-300"
                      : "bg-red-300"}"
              ></span>
              ${passed ? "Zdałeś test" : "Nie zdałeś testu"}
            </span>
            <span class="text-xs text-white/80">
              Możesz przejrzeć swoje odpowiedzi poniżej.
            </span>
          </div>
        </div>

        <!-- Lista pytań jako kółka -->
        <div class="space-y-3">
          <div class="text-sm text-gray-700">Przegląd pytań:</div>
          <div class="flex flex-wrap gap-2">
            ${questions.map((q, i) => {
              const ans = answers[String(i)];
              const correct = q?.correct;

              // Kolory jak wcześniej:
              // żółty = brak odpowiedzi, zielony = poprawna, czerwony = błędna
              let colorClasses =
                  "bg-yellow-100 text-yellow-800 border-yellow-200"; // domyślnie brak odpowiedzi
              if (ans) {
                colorClasses =
                    ans === correct
                        ? "bg-green-100 text-green-800 border-green-200"
                        : "bg-red-100 text-red-800 border-red-200";
              }

              return html`
                <button
                    aria-label=${ans
                        ? `Pytanie ${i + 1}: odpowiedź ${ans}`
                        : `Pytanie ${i + 1}: Brak odpowiedzi`}
                    class="
                    flex h-9 w-9 items-center justify-center rounded-full border text-xs font-medium
                    shadow-sm transition ${colorClasses}
                  "
                    @click=${() => (this.showDetailIndex = i)}
                >
                  ${i + 1}
                </button>
              `;
            })}
          </div>
          <div class="flex flex-wrap gap-x-4 gap-y-2 text-xs text-gray-600" aria-label="Legenda odpowiedzi">
            <span><span class="mr-1 inline-block h-2.5 w-2.5 rounded-full bg-green-200"></span>Poprawna odpowiedź</span>
            <span><span class="mr-1 inline-block h-2.5 w-2.5 rounded-full bg-red-200"></span>Błędna odpowiedź</span>
            <span><span class="mr-1 inline-block h-2.5 w-2.5 rounded-full bg-yellow-200"></span>Brak odpowiedzi</span>
          </div>
        </div>

        <!-- Szczegóły pytania -->
        ${this.showDetailIndex != null
            ? this._renderDetail(this.showDetailIndex)
            : null}

        <div class="pt-2 flex flex-wrap items-center gap-3">
          <button class="btn btn-primary" @click=${() => this.startQuiz()}>
            Rozpocznij nowy test
          </button>
        </div>
      </div>
    `;
  }

  _renderDetail(i) {
    const q = this.quiz.questions?.[i];
    const ans = this.quiz.answers?.[String(i)];
    if (!q) return null;

    const isTF = q.kind === "tf" || q.kind === "boolean";

    const renderOptions = () => {
      const opts = isTF ? ["T", "N"] : ["A", "B", "C"];
      const heading = isTF ? "Odpowiedzi TAK / NIE:" : "Odpowiedzi A/B/C:";

      return html`
        <div class="mt-3 space-y-2">
          <div class="text-sm text-gray-600">${heading}</div>
          <div class="space-y-2">
            ${opts.map((opt) => {
              const label = isTF ? (opt === "T" ? "TAK" : "NIE") : opt;
              const text = isTF ? "" : q[opt.toLowerCase()] || "";

              const isCorrect = q.correct === opt;
              const isUser = ans === opt;

              const base = "flex items-start gap-3 rounded-lg px-3 py-2 text-sm";
              let state = "bg-white/80 border border-gray-200";
              if (isCorrect) state = "bg-emerald-50 border border-emerald-200";
              else if (isUser) state = "bg-red-50 border border-red-200";

              return html`
                <div class="${base} ${state}">
                  <span
                      class="
                      mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-full border text-[11px] font-semibold
                      ${isCorrect
                          ? "border-emerald-300 bg-emerald-100 text-emerald-800"
                          : isUser
                              ? "border-red-300 bg-red-100 text-red-800"
                              : "border-primary/20 bg-primary-50 text-primary-700"}
                    "
                  >
                    ${isTF ? opt : label}
                  </span>
                  <div
                      class="${isCorrect
                          ? "text-emerald-900"
                          : isUser
                              ? "text-red-900"
                              : "text-gray-900"}"
                  >
                    <div class="font-medium">
                      ${label}
                    </div>
                    ${!isTF
                        ? html`
                          <div class="text-xs text-gray-700">
                            ${text ||
                            html`<span class="text-gray-400 italic"
                            >(brak treści)</span
                            >`}
                          </div>
                        `
                        : null}
                  </div>
                </div>
              `;
            })}
          </div>
        </div>
      `;
    };

    return html`
      <div
          class="mt-4 rounded-2xl bg-gray-50 px-4 py-4 sm:px-5 sm:py-5 space-y-3"
      >
        <div class="flex items-start justify-between gap-3">
          <div class="font-medium">
            Szczegóły pytania ${i + 1}${q.nr ? html` (nr ${q.nr})` : ""}
          </div>
          <button
              class="text-xs text-gray-500 hover:text-gray-700"
              @click=${() => (this.showDetailIndex = null)}
          >
            Zamknij podgląd
          </button>
        </div>

        <div class="text-sm text-gray-900">
          ${q.question}
        </div>

        ${q.media
            ? (() => {
              const mediaUrl = resolvedMediaUrl(q.media);

              return /\.(jpg|jpeg|png|gif)$/i.test(q.media)
                  ? html`
            <div class="mt-2 overflow-hidden rounded-xl border border-gray-200 bg-gray-50">
              <img
                src="${mediaUrl}"
                alt="Ilustracja do pytania"
                class="max-h-72 w-full object-contain"
              />
            </div>
          `
                  : isMp4Media(q.media)
                      ? html`
              <div class="mt-2 overflow-hidden rounded-xl border border-gray-200 bg-black/5">
                <video
                  class="w-full rounded-xl"
                  src="${mediaUrl}"
                  controls
                ></video>
              </div>
            `
                      : null;
            })()
            : null}

        <div class="mt-2 grid gap-2 text-sm sm:grid-cols-2">
          <div class="text-gray-600">
            Poprawna odpowiedź:
            <span class="font-semibold text-gray-900">
              ${q.correct === "T" ? "TAK" : q.correct === "N" ? "NIE" : q.correct}
            </span>
          </div>

          <div class="text-gray-600">
            Twoja odpowiedź:
            <span class="font-semibold">
              ${ans === "T"
                  ? "TAK"
                  : ans === "N"
                      ? "NIE"
                      : ans ??
                      html`<span class="text-yellow-700">Brak odpowiedzi</span>`}
            </span>
          </div>
        </div>

        ${renderOptions()}

        ${q.explanation
            ? html`
              <div class="mt-3 text-sm text-gray-700">
                <span class="font-medium">Wyjaśnienie:</span>
                <span class="block mt-1 text-gray-700">
                  ${q.explanation}
                </span>
              </div>
            `
            : null}
      </div>
    `;
  }

  // 🔑 helper: nie przechwytujemy klawiatury na polach tekstowych, selectach, edytowalnych oraz video
  _isInteractiveTarget(target) {
    if (!target) return false;
    const tag = target.tagName?.toLowerCase();
    const editable = target.getAttribute?.("contenteditable");

    if (
        tag === "input" ||
        tag === "textarea" ||
        tag === "select" ||
        editable === "" ||
        editable === "true"
    ) {
      return true;
    }

    if (tag === "video") {
      return true;
    }

    return false;
  }

  // 🔑 globalna obsługa klawiatury (WCAG 2.1)
  _onKeydown(event) {
    // nie ruszamy skrótów systemowych
    if (event.altKey || event.metaKey || event.ctrlKey) return;

    if (this.finishConfirmationOpen) {
      if (event.key === "Escape" && !this._actionLock) {
        event.preventDefault();
        this.closeFinishConfirmation();
      } else if (event.key === "Tab") {
        this._trapFinishDialogFocus(event);
      }
      return;
    }

    if (this.state !== "running" || !this.quiz) return;

    if (this._isInteractiveTarget(event.target)) return;

    const key = event.key.toLowerCase();
    const q = this.quiz.current_question;
    if (!q) return;

    const isTF = q.kind === "tf" || q.kind === "boolean";

    // ESC -> przerwij test
    if (key === "escape") {
      event.preventDefault();
      this.interrupt();
      return;
    }

    // Enter / Spacja -> następne pytanie; na ostatnim pytaniu otwórz potwierdzenie
    if (key === "enter" || key === " ") {
      event.preventDefault();
      this.next();
      return;
    }

    if (key === "arrowleft") {
      event.preventDefault();
      this.prev();
      return;
    }

    if (key === "arrowright") {
      event.preventDefault();
      this.next();
      return;
    }

    // wybór odpowiedzi
    if (isTF) {
      // TAK/NIE: T/N lub 1/2
      if (key === "t" || key === "1") {
        event.preventDefault();
        this.currentAnswer = "T";
        this.requestUpdate();
        return;
      }
      if (key === "n" || key === "2") {
        event.preventDefault();
        this.currentAnswer = "N";
        this.requestUpdate();
        return;
      }
    } else {
      // A/B/C: 1/2/3 lub A/B/C
      if (key === "1" || key === "a") {
        event.preventDefault();
        this.currentAnswer = "A";
        this.requestUpdate();
        return;
      }
      if (key === "2" || key === "b") {
        event.preventDefault();
        this.currentAnswer = "B";
        this.requestUpdate();
        return;
      }
      if (key === "3" || key === "c") {
        event.preventDefault();
        this.currentAnswer = "C";
        this.requestUpdate();
        return;
      }
    }
  }

  async startQuiz() {
    if (this.loading) return;

    try {
      this.error = null;
      this.loading = true;
      const body = {};
      const resp = await fetch("/api/quizzes", {
        method: "POST",
        headers: jsonHeaders(),
        body: JSON.stringify(body),
      });
      const createError = quizCreateError(resp);
      if (createError) throw createError;
      if (!resp.ok) throw new Error("Nie udało się utworzyć quizu");
      const data = await resp.json();
      const participantToken = data.participant_token;
      delete data.participant_token;
      const previousId = loadAttemptReference("last_quiz_id");
      if (previousId && previousId !== data.id) {
        removeAttemptToken("quiz", previousId);
      }
      storeAttemptToken("quiz", data.id, participantToken);
      this.quiz = data;
      this.state = "running";
      this.currentAnswer = null;
      this.showDetailIndex = null;
      // remember last quiz id for guests too
      storeAttemptReference("last_quiz_id", data.id);
      this._setupTimer();
      this.requestUpdate();
    } catch (e) {
      this.error = e.message;
    } finally {
      this.loading = false;
    }
  }

  async continueLast() {
    const localLast = loadAttemptReference("last_quiz_id");
    const lastId = this.lastQuizId || localLast;
    if (lastId) return this.continueQuiz(lastId);
    this.error = "Brak zapisanego quizu do kontynuowania";
  }

  async continueQuiz(id) {
    let accessDenied = false;
    try {
      this.loading = true;
      const resp = await fetch(`/api/quizzes/${id}`, {
        headers: this._quizHeaders(id),
      });
      if (!resp.ok) {
        accessDenied = resp.status === 404;
        throw new Error("Nie znaleziono quizu");
      }
      const data = await resp.json();
      if (data.status === "completed") {
        this.quiz = data;
        this.currentAnswer = null;
        this.state = "summary";
        this._clearTimer();
      } else {
        this.quiz = data;
        this.currentAnswer = data.answers?.[String(data.current_question_index)] ?? null;
        this.state = "running";
        this._setupTimer();
      }
      storeAttemptReference("last_quiz_id", data.id);
    } catch (e) {
      this.error = e.message;
      this.state = "intro";
      this.quiz = null;
      this._clearTimer();
      if (accessDenied) {
        removeAttemptReference("last_quiz_id");
        removeAttemptToken("quiz", id);
      }
    } finally {
      this.loading = false;
    }
  }

  // Serialize actions to avoid races between timeout and user clicks
  async _withLock(fn) {
    if (this._actionLock) return;
    this._actionLock = true;
    try {
      await fn();
    } finally {
      this._actionLock = false;
      // If a timeout occurred during the locked action, handle it now
      const queued = this._queuedTimeoutSeconds || 0;
      this._queuedTimeoutSeconds = 0;
      if (queued > 0 && this.state === "running") {
        // run auto-advance after releasing the lock
        this._autoAdvanceOnTimeout(queued);
      }
    }
  }

  async next() {
    if (!this.quiz) return;
    const idx = this.quiz.current_question_index;
    const total = this.quiz.total_questions;
    if (idx + 1 >= total) return this.openFinishConfirmation();

    return this._withLock(async () => this._saveAnswerAndAdvance());
  }

  async prev() {
    if (!this.quiz) return;
    if ((this.quiz.current_question_index || 0) <= 0) return;

    return this._withLock(async () => {
      await this._saveAnswer({ retreat: true });
    });
  }

  async finish() {
    // Publiczne API: opakuj zakończenie w blokadę, aby serializować z innymi akcjami
    return this._withLock(async () => {
      await this._performFinish();
    });
  }

  openFinishConfirmation(event = null) {
    if (this.state !== "running" || !this.quiz || this.loading || this._actionLock) return;
    this.error = null;
    this._finishTrigger = event?.currentTarget || document.activeElement;
    this.finishConfirmationOpen = true;
    this.requestUpdate();
  }

  closeFinishConfirmation() {
    if (this.loading || this._actionLock) return;
    const trigger = this._finishTrigger;
    this.finishConfirmationOpen = false;
    this.requestUpdate();
    this.updateComplete.then(() => trigger?.isConnected && trigger.focus());
  }

  confirmFinish() {
    return this.finish();
  }

  _trapFinishDialogFocus(event) {
    const dialog = this.querySelector("[data-finish-dialog]");
    if (!dialog) return;
    const focusable = [...dialog.querySelectorAll("button:not([disabled])")];
    if (focusable.length === 0) {
      event.preventDefault();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  async _performFinish() {
    try {
      this.error = null;
      this.loading = true;
      this.requestUpdate();
      const saved = await this._saveAnswer();
      if (!saved) {
        this.error = "Nie udało się zapisać odpowiedzi. Spróbuj ponownie.";
        return false;
      }
      if (saved.status === "completed") {
        this._applyCompletedQuiz(saved);
        return true;
      }

      const resp = await fetch(`/api/quizzes/${this.quiz.id}/finish`, {
        method: "POST",
        headers: this._quizHeaders(),
      });
      if (!resp.ok) throw new Error("Nie udało się zakończyć quizu");
      const data = await resp.json();
      this._applyCompletedQuiz(data);
      return true;
    } catch (_error) {
      return this._recoverAfterFinishFailure();
    } finally {
      this.loading = false;
      this.requestUpdate();
    }
  }

  async _recoverAfterFinishFailure() {
    try {
      const resp = await fetch(`/api/quizzes/${this.quiz.id}`, {
        headers: this._quizHeaders(),
      });
      if (resp.ok) {
        const data = await resp.json();
        if (data.status === "completed") {
          this._applyCompletedQuiz(data);
          return true;
        }
        this.quiz = data;
        this.currentAnswer = data.answers?.[String(data.current_question_index)] ?? null;
        this._syncTimerFromQuiz();
      }
    } catch (_error) {
      // Zachowaj lokalny stan i pozwól użytkownikowi świadomie ponowić zakończenie.
    }
    this.error = "Nie udało się zakończyć testu. Sprawdź połączenie i spróbuj ponownie.";
    return false;
  }

  _applyCompletedQuiz(data) {
    this.quiz = data;
    this.currentAnswer = null;
    this.state = "summary";
    this.finishConfirmationOpen = false;
    this._clearTimer();
    this.requestUpdate();
  }

  async interrupt() {
    const exec = async () => {
      try {
        // wyślij tylko inkrement czasu (o ile działa timer)
        const seconds_increment = this._consumeElapsedSeconds();
        const resp = await fetch(`/api/quizzes/${this.quiz.id}/interrupt`, {
          method: "POST",
          headers: this._quizHeaders(),
          body: JSON.stringify({ seconds_increment }),
        });
        if (!resp.ok) throw new Error("Nie udało się zapisać stanu quizu");
        await resp.json();
        // Ustaw tryb przerwania: wróć do ekranu wstępnego z opcją kontynuacji
        this.error = null;
        // zapamiętaj ostatni quiz (dla gościa i zgodnie z wymaganiem)
        if (this.quiz?.id) {
          storeAttemptReference("last_quiz_id", this.quiz.id);
        }
        this._clearTimer();
        this.state = "intro";
        this.currentAnswer = null;
        // nie czyść quiz.id z localStorage — pozwala kontynuować później
        this.requestUpdate();
      } catch (e) {
        this.error = e.message;
      }
    };
    return this._withLock(exec);
  }

  async _saveAnswerAndAdvance() {
    await this._saveAnswer({ advance: true });
  }

  async _saveAnswer(opts = {}) {
    const { advance, retreat } = opts || {};
    let seconds_increment = 0;
    try {
      seconds_increment = this._consumeElapsedSeconds();
      const body = {
        seconds_increment,
      };
      if (this.currentAnswer !== null && this.currentAnswer !== undefined) {
        body.answer = this.currentAnswer;
      }
      if (advance === true) body.advance = true;
      if (retreat === true) body.retreat = true;
      const resp = await fetch(`/api/quizzes/${this.quiz.id}`, {
        method: "PATCH",
        headers: this._quizHeaders(),
        body: JSON.stringify(body),
      });
      if (!resp.ok) throw new Error("Nie udało się zapisać odpowiedzi");
      const data = await resp.json();
      this.quiz = data;
      this.currentAnswer = null;
      this._syncTimerFromQuiz();
      if (data.status === "completed") {
        this.state = "summary";
        this.finishConfirmationOpen = false;
        this._clearTimer();
      }
      this.requestUpdate();
      return data;
    } catch (e) {
      this.error = e.message;
      this._elapsedSinceSync = (this._elapsedSinceSync || 0) + seconds_increment;
      return null;
    }
  }

  _setupTimer() {
    this._clearTimer();
    if (!this._hasQuizTimeLimit()) return;
    this._syncTimerFromQuiz();
    const tick = () => {
      this.timerTick++;
      const elapsed = (this._elapsedSinceSync = (this._elapsedSinceSync || 0) + 1);
      if (typeof this._remainingSeconds === "number") {
        this._remainingSeconds = Math.max(0, this._remainingSeconds - 1);
      }
      if (this._currentRemainingSeconds() <= 0) {
        const secondsInc = Math.max(elapsed, 0);
        this._elapsedSinceSync = 0;
        if (this._actionLock) {
          this._queuedTimeoutSeconds = Math.max(
              this._queuedTimeoutSeconds || 0,
              secondsInc,
          );
        } else {
          this._autoAdvanceOnTimeout(secondsInc);
        }
        return;
      }

      this.requestUpdate();
    };
    this._elapsedSinceSync = 0;
    this._timerId = window.setInterval(tick, 1000);
  }

  _consumeElapsedSeconds() {
    const s = this._elapsedSinceSync || 0;
    this._elapsedSinceSync = 0;
    return s;
  }

  _clearTimer() {
    if (this._timerId) {
      window.clearInterval(this._timerId);
      this._timerId = null;
    }
    this._elapsedSinceSync = 0;
    this._remainingSeconds = null;
  }

  async _autoAdvanceOnTimeout(secondsInc = 0) {
    // If another action is in progress, re-queue and exit to avoid race
    if (this._actionLock) {
      this._queuedTimeoutSeconds = Math.max(
          this._queuedTimeoutSeconds || 0,
          secondsInc || 0,
      );
      return;
    }
    if (this.state !== "running" || !this.quiz) return;
    return this._withLock(async () => {
      try {
        const body = {
          seconds_increment: secondsInc,
        };
        if (this.currentAnswer !== null && this.currentAnswer !== undefined) {
          body.answer = this.currentAnswer;
        }
        const saveResp = await fetch(`/api/quizzes/${this.quiz.id}`, {
          method: "PATCH",
          headers: this._quizHeaders(),
          body: JSON.stringify(body),
        });
        if (!saveResp.ok) {
          throw new Error("Nie udało się zapisać odpowiedzi przed zakończeniem");
        }
        const saved = await saveResp.json();
        this.quiz = saved;

        if (saved.status === "completed") {
          this.state = "summary";
          this._clearTimer();
          this.currentAnswer = null;
          this.requestUpdate();
          return;
        }

        const finishResp = await fetch(`/api/quizzes/${this.quiz.id}/finish`, {
          method: "POST",
          headers: this._quizHeaders(),
        });
        if (!finishResp.ok) {
          throw new Error("Nie udało się zakończyć quizu po upływie czasu");
        }
        const finished = await finishResp.json();
        this.quiz = finished;
        this.state = "summary";
        this._clearTimer();
        this.currentAnswer = null;
        this.requestUpdate();
      } catch (e) {
        this.error = e.message;
      }
    });
  }

  _hasQuizTimeLimit() {
    return Number(this.quiz?.duration_seconds || 0) > 0;
  }

  _quizHeaders(id = this.quiz?.id) {
    return attemptHeaders(jsonHeaders(), "quiz", id);
  }

  _syncTimerFromQuiz() {
    if (!this.quiz) {
      this._remainingSeconds = null;
      return;
    }

    const remaining = this.quiz?.time_remaining_seconds;
    if (remaining === null || remaining === undefined) {
      const duration = Number(this.quiz?.duration_seconds || 0);
      const spent = Number(this.quiz?.seconds_passed || 0);
      this._remainingSeconds = duration > 0 ? Math.max(0, duration - spent) : null;
      return;
    }

    this._remainingSeconds = Math.max(0, Number(remaining));
  }

  _currentRemainingSeconds() {
    if (typeof this._remainingSeconds === "number") return this._remainingSeconds;

    const duration = Number(this.quiz?.duration_seconds || 0);
    const spent = Number(this.quiz?.seconds_passed || 0);
    return duration > 0 ? Math.max(0, duration - spent) : 0;
  }

  _formatDuration(totalSeconds) {
    const seconds = Math.max(0, Number(totalSeconds || 0));
    const mm = Math.floor(seconds / 60);
    const ss = seconds % 60;
    if (mm < 60) return `${mm}:${String(ss).padStart(2, "0")}`;

    const hh = Math.floor(mm / 60);
    const rest = mm % 60;
    return `${hh}h ${rest}m`;
  }
}

customElements.define("quiz-app", QuizApp);
